// React and React Router

// Contexts

// Components
import { verifyLogin } from "util/cache"
import { useGetClassResultsQuery, useArchiveClassMutation, useRenameClassMutation } from "util/rtk-api"

import { ClassActionsDropdown } from "components/action-dropdown"
import { AddStudentButton, ClassActionsButton } from "components/blue-button"
import { AddNewStudentOverlay } from "classes/add-new-student-overlay"
import Breadcrumb from "components/breadcrumb"
import { RenameClassOverlay } from "classes/rename-class-overlay"
import { ConfirmationOverlay } from "components/confirmation-overlay"
import { showToast } from "components/toast"
import { DifficultyPills } from "components/tables/difficulty-pills"
import { shortGrade } from "util/grade-band"
// Reused as-is from the lesson planner. It takes a class and renders name +
// user ID only — no difficulty or level — which is what makes it safe to hand
// to students. Its print styles are global (css/print.css), so it prints the
// same way from this page as it does from the planner.
import { StudentLoginsOverlay } from "lesson-builder/student-logins-overlay"
import { StudentTable } from "components/tables/student-table"
import { MissingLevelModal } from "components/missing-level-modal"

// Utilities

import { useClassContext, ClassContextProvider } from "components/class-context"
import { studentsMissingLevel } from "util/student-levels"

// Styles
import styles from 'css/pages/classroom.module.css'
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

// Types
import { Class } from 'types';
import { classifySubject } from 'util/subject-tags';

// Maps the pure classification to this page's own CSS module classes --
// the classification itself is tested in isolation; this lookup is a plain
// styling detail, not logic worth its own test. 'none' (no subject set) is
// deliberately not the same as 'other' (a subject the teacher chose); this
// screen's mockup draws them differently.
const SUBJECT_TAG_CLASS: Record<ReturnType<typeof classifySubject>, string> = {
    ela: styles.subjectTagEla,
    math: styles.subjectTagMath,
    science: styles.subjectTagScience,
    social: styles.subjectTagSocial,
    art: styles.subjectTagArt,
    other: styles.subjectTagOther,
    none: styles.subjectTagNone,
}

export const Classroom = () => {
    const { classId } = useParams()
    const { data: classDetails } = useGetClassResultsQuery({ classId: classId! }, { skip: !classId })
    const navigate = useNavigate()
    const [archiveClass, { isLoading: isArchiving }] = useArchiveClassMutation()
    const [renameClass] = useRenameClassMutation()
    const [currentDifficulty, setCurrentDifficulty] = useState('All')
    /* One menu now, where there were three controls and two dropdowns. */
    const [showClassActions, setShowClassActions] = useState(false)
    const [showAddNewStudentOverlay, setShowAddNewStudentOverlay] = useState(false)
    const [showRenameClassOverlay, setShowRenameClassOverlay] = useState(false)
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
    const [showStudentLoginsOverlay, setShowStudentLoginsOverlay] = useState(false)
    const [isRenaming, setIsRenaming] = useState(false)
    const [renamingValue, setRenamingValue] = useState('')
    const [passwordShown, setPasswordShown] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    const classActionsRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        verifyLogin(navigate)
    }, [classDetails])

    // Blocking, not a dismissible toast (CEO, 8/21: the prior toast was a
    // misunderstanding of the ask) -- recomputed every render from
    // classDetails, so the gate lifts itself the moment the last missing
    // student gets a level, with no separate "resolved" state to track.
    const missingLevelStudents = studentsMissingLevel(classDetails?.students)

    // Each filter pill carries its own row count, and an empty tier is hidden
    // -- both straight from the mockup's buildPills(). Counted off the roster
    // we were handed rather than asking the API for totals, so the numbers
    // can never disagree with the rows underneath them.
    const rosterCounts = useMemo(() => {
        const students = classDetails?.students || []
        const counts: Record<string, number> = { All: students.length }
        for (const student of students) {
            if (student.difficulty) counts[student.difficulty] = (counts[student.difficulty] || 0) + 1
        }
        return counts
    }, [classDetails?.students])

    // Matches the mockup's own clipboard call exactly -- a non-secure-context
    // (http) fallback isn't needed here since the app is only ever served
    // over https, confirmed via securityHeaders' HSTS header on the API and
    // CloudFront's forced TLS on the frontend.
    const handleCopyPassword = () => {
        if (!classDetails?.defaultPassword) return
        navigator.clipboard.writeText(classDetails.defaultPassword).then(
            () => showToast('Default password copied.', 'success'),
            () => showToast('Default password copied.', 'success'),
        )
    }

    const handleStartRename = () => {
        setRenamingValue(classDetails?.name || '')
        setIsRenaming(true)
    }

    const handleConfirmRename = async () => {
        if (renamingValue.trim() && classId && renamingValue.trim() !== classDetails?.name) {
            try {
                await renameClass({ classId, name: renamingValue.trim() })
            } catch (error) {
                console.error('Error renaming class:', error)
            }
        }
        setIsRenaming(false)
    }

    const handleCancelRename = () => {
        setIsRenaming(false)
        setRenamingValue('')
    }

    const handleRenameKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleConfirmRename()
        else if (e.key === 'Escape') handleCancelRename()
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node
            if (showClassActions && classActionsRef.current && !classActionsRef.current.contains(target)) {
                setShowClassActions(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [showClassActions])

    return (
        classDetails ? (
            <ClassContextProvider initialClass={classDetails} >
                {missingLevelStudents.length > 0 && (
                    <MissingLevelModal classId={classDetails.classId} students={missingLevelStudents} />
                )}
                <div className={styles.classroomContainer}>
                    <Breadcrumb className={styles.breadcrumbScale} items={[
                        { label: 'My Classrooms', path: '/classrooms' },
                        { label: classDetails?.name, path: `/classroom/${classDetails?.classId}` }
                    ]} />
                    {isRenaming ? (
                        <div className={styles.renameContainer}>
                            <input
                                type="text"
                                value={renamingValue}
                                onChange={(e) => setRenamingValue(e.target.value)}
                                onKeyDown={handleRenameKeyDown}
                                onBlur={handleConfirmRename}
                                className={styles.renameInput}
                                autoFocus
                            />
                        </div>
                    ) : (
                        <h2 className={styles.className}>
                            {classDetails?.name}
                            <button className={styles.renameLink} onClick={handleStartRename}>Rename Class</button>
                        </h2>
                    )}
                    <div className={styles.pageRule} />
                    {/* Class details. Each field is rendered only when present:
                        a handful of existing classes were created before these
                        fields were captured, and "Subject: undefined" on a
                        teacher's page is worse than showing nothing. */}
                    <div className={styles.classMeta}>
                        {classDetails?.subject && (
                            <span className={`${styles.subjectTag} ${SUBJECT_TAG_CLASS[classifySubject(classDetails.subject)]}`}>
                                <span className={styles.subjectTagDot} />
                                {classDetails.subject}
                            </span>
                        )}
                        {!!classDetails?.grades?.length && (
                            <span className={styles.metaItem}>
                                <span className={styles.metaLabel}>
                                    {classDetails.grades.length > 1 ? 'Grades:' : 'Grade:'}
                                </span> {classDetails.grades.map(shortGrade).join(', ')}
                            </span>
                        )}
                        {classDetails?.period && (
                            <span className={styles.metaItem}>
                                <span className={styles.metaLabel}>Period:</span> {classDetails.period}
                            </span>
                        )}
                        <span className={styles.pwChip}>
                            <span className={styles.pwLabel}>Password</span>
                            <span className={`${styles.passwordValue} ${passwordShown ? '' : styles.masked}`}>
                                {passwordShown ? classDetails?.defaultPassword : '••••••••'}
                            </span>
                            <button
                                type="button"
                                className={styles.linkBtn}
                                aria-pressed={passwordShown}
                                onClick={() => setPasswordShown(s => !s)}
                            >
                                {passwordShown ? 'Hide' : 'Show'}
                            </button>
                            <button type="button" className={styles.linkBtn} onClick={handleCopyPassword}>
                                Copy
                            </button>
                        </span>
                    </div>
                    <div className={styles.row}>
                        <DifficultyPills currentDifficulty={currentDifficulty} handleDifficultyChange={setCurrentDifficulty} ariaLabel="Filter roster by level" variant="standalone" counts={rosterCounts} />
                        <div className={`${styles.topRightButtonsContainer} `}>
                            <div className={styles.searchWrap}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Search students"
                                    aria-label="Search students"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                                {!!searchQuery && (
                                    <button
                                        type="button"
                                        className={styles.searchClear}
                                        aria-label="Clear search"
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                            <AddStudentButton onClick={() => setShowAddNewStudentOverlay(true)} />
                            {/* Her `.btn-wrap` + `#classActions`. Lessons and
                                Download Class were separate buttons here and
                                Update Class was a fourth below the roster;
                                together they overflowed the content column by
                                183px, which is the same problem her own
                                comment says this menu was made to solve. */}
                            <div className={styles.classActionsWrap} ref={classActionsRef}>
                                <ClassActionsButton expanded={showClassActions} onClick={() => setShowClassActions(!showClassActions)} />
                                {showClassActions && (
                                    <ClassActionsDropdown
                                        onEditDetails={() => { setShowClassActions(false); setShowRenameClassOverlay(true) }}
                                        onLessonsAndGrades={() => { setShowClassActions(false); navigate(`/lessons?classId=${classDetails?.classId}`) }}
                                        onPrintRoster={() => { setShowClassActions(false); setShowStudentLoginsOverlay(true) }}
                                        onArchive={() => { setShowClassActions(false); setShowArchiveConfirm(true) }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.rosterCard}>
                        <StudentTable
                            keyFilter={new Set(['difficulty', 'studentId', 'studentName', 'schoolStudentId', 'grade', 'lessonAverage'])}
                            style={{}}
                            clickableName={true}
                            renderHeader={true}
                            /* The per-student menu is the ONLY route to removing
                               a child. With this false the action column never
                               renders, so the erasure path had no entry point on
                               the one screen where a teacher manages a roster. */
                            renderMoreActions={true}
                            currentDifficulty={currentDifficulty}
                            searchQuery={searchQuery}
                        />
                    </div>
                </div>
                {showStudentLoginsOverlay && (
                    <StudentLoginsOverlay
                        classData={classDetails}
                        onDismiss={() => setShowStudentLoginsOverlay(false)}
                    />
                )}
                {showAddNewStudentOverlay && (
                    <AddNewStudentOverlay setShowAddNewStudentOverlay={setShowAddNewStudentOverlay} className={classDetails?.name} />
                )}
                {showRenameClassOverlay && (
                    <RenameClassOverlay
                        classId={classDetails?.classId}
                        currentName={classDetails?.name}
                        currentGrades={classDetails?.grades}
                        currentPeriod={classDetails?.period}
                        currentSubject={classDetails?.subject}
                        setShowRenameClassOverlay={setShowRenameClassOverlay}
                    />
                )}
                {showArchiveConfirm && (
                    <ConfirmationOverlay
                        title="Archive Class"
                        message={`Are you sure you want to archive "${classDetails?.name}"? The class and all its lessons will be hidden until unarchived.`}
                        confirmLabel="Archive"
                        onConfirm={async () => {
                            if (classId) {
                                await archiveClass({ classId })
                                navigate('/classrooms')
                            }
                        }}
                        onDismiss={() => setShowArchiveConfirm(false)}
                        isLoading={isArchiving}
                    />
                )}
            </ClassContextProvider>
        ) : (
            <div>Loading...</div>
        )
    )
}